import type { TFunction } from 'i18next';
import { formatMoney, formatDate } from '@/utils/currency';

export type QuickInsight = {
  code?: string;
  type?: 'positive' | 'negative' | 'neutral' | string;
  params?: Record<string, unknown>;
  text?: string;
};

/** Translate structured AI quick-insight payloads; fall back to English `text`. */
export function formatQuickInsight(t: TFunction, insight: QuickInsight): string {
  const code = insight.code;
  const params = { ...(insight.params || {}) } as Record<string, unknown>;

  if (!code) return insight.text || '';

  if (params.trend === 'increased' || params.trend === 'decreased') {
    params.trend = t(`analytics.${params.trend}`);
  }

  if (typeof params.date === 'string' && params.date) {
    params.date = formatDate(params.date) || params.date;
  }

  if (typeof params.day === 'string' && params.day) {
    const dayKey = `analytics.weekdays.${String(params.day).toLowerCase()}`;
    const translated = t(dayKey);
    params.day = translated !== dayKey ? translated : params.day;
  }

  for (const moneyKey of ['amount'] as const) {
    if (params[moneyKey] != null && params[moneyKey] !== '') {
      params[moneyKey] = formatMoney(Number(params[moneyKey]));
    }
  }

  if (code === 'unpaid_with_customers') {
    const count = Number(params.count) || 0;
    const others = Number(params.others) || 0;
    params.othersSuffix = others > 0 ? t('analytics.insights.andOthers', { count: others }) : '';
    const key = count === 1 ? 'analytics.insights.unpaid_with_customers_one' : 'analytics.insights.unpaid_with_customers';
    return t(key, params);
  }

  if (code === 'unpaid') {
    const count = Number(params.count) || 0;
    const key = count === 1 ? 'analytics.insights.unpaid_one' : 'analytics.insights.unpaid';
    return t(key, params);
  }

  const key = `analytics.insights.${code}`;
  const translated = t(key, params);
  if (translated === key) return insight.text || '';
  return translated;
}
